import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../../Generic/Button";
import Card from "../../Generic/Card";
import { Table, Thead, Trow, Tcol, Tbody } from "../../Generic/Table";
import { errorNotification, successNotification } from "../../Generic/Notifications";
import Loader from "../../Generic/Loader";
import EmptyState from "../../Generic/EmptyState";
import StudioService from "../Services/StudioService";
import { AppRoutes, DashboardModules, StudioSubModules } from "../../config/app.config";
import { useDish } from "./context/DishContext";
import { dishTagKeysFromApi, dishTagLabel } from "./utils";
import { zIndex } from "../../config/zIndex";

const Page = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0 1rem 1rem 1rem;
	gap: 1.5rem;
	padding: 1.5rem;
`;

const DishDetailsContentColumn = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const DishDetailsPageHeader = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	gap: 1rem;
`;

const DishDetailsMainCard = styled(Card)`
	width: 100%;
	margin: 0;
	padding: 1.5rem;
	flex-direction: column;
`;

const DishDetailsCardHeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	min-width: 0;
	flex: 1;
`;

const DishDetailsCardHeaderBackButton = styled.span.attrs({
	className: "material-symbols-outlined",
})`
	cursor: pointer;
	color: #000;
	flex-shrink: 0;
`;

const DishDetailsHeaderTitle = styled.div`
	font-size: 2rem;
	font-weight: 600;
	color: #000;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const DishDetailsHeroRow = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1.5rem;
	@media (min-width: 600px) {
		flex-direction: row;
		align-items: stretch;
		gap: 2rem;
	}
`;

const DishDetailsImageColumn = styled.div`
	display: flex;
	justify-content: center;
	align-items: flex-start;
	flex-shrink: 0;
	@media (min-width: 600px) {
		justify-content: flex-start;
		flex: 0 0 min(42%, 22rem);
		max-width: 22rem;
		align-self: flex-start;
	}
`;

const DishDetailsHeroImage = styled.img`
	width: 100%;
	max-width: 20rem;
	height: auto;
	min-height: 12rem;
	max-height: 22rem;
	object-fit: contain;
	@media (min-width: 600px) {
		max-width: 100%;
		min-height: 14rem;
		max-height: 26rem;
	}
`;

const DishDetailsTextColumn = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
	gap: 1.25rem;
	justify-content: center;
	min-height: 0;
`;

const DishDetailsActionsColumn = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: flex-start;
	flex-wrap: wrap;
	gap: 0.5rem;
	align-self: flex-start;
	flex-shrink: 0;
	width: auto;
`;

const DishDetailsDeleteButton = styled(Button)`
	background-color:#ee5e68;
	color: #fff;
	box-shadow:none;

	&:hover:not(:disabled) {
		background-color: #b71c1c;
		color: #fff;
		box-shadow:none;
		transform: translateY(-2px);
	}

	&:focus:not(:disabled) {
		box-shadow:none;
		color: #fff;
	}

	&:active:not(:disabled) {
		background-color: #8e0000;
		color: #fff;
		box-shadow: none;
		transform: translateY(2px);
	}
`;

const DishDetailsTag = styled.div`
	background: rgba(153, 153, 153, 0.8);
	border-radius: 3px 0 0 3px;
	color: #fff;
	display: inline-block;
	height: 26px;
	line-height: 26px;
	padding: 0 10px 0 23px;
	margin: 0 10px 10px 0;
	position: relative;
	-webkit-transition: color 0.2s;
	font-size: 14px;
	&:before {
		background: #fff;
		border-radius: 10px;
		content: "";
		height: 6px;
		left: 10px;
		position: absolute;
		width: 6px;
		top: 10px;
	}
	&:after {
		background: #fff;
		border-bottom: 13px solid transparent;
		border-left: 10px solid rgba(153, 153, 153, 0.8);
		border-top: 13px solid transparent;
		content: "";
		position: absolute;
		right: 0;
		top: 0;
	}
`;

const DishDetailsTagList = styled.div`
	display: flex;
	flex-wrap: wrap;
	width: 100%;
	justify-content: flex-start;
`;

const DishDetailsFieldLabel = styled.span`
	color: rgba(0, 0, 0, 0.6);
	font-size: 0.875rem;
`;

const DishDetailsFieldValue = styled.span`
	font-weight: 600;
	margin-top: 0.35rem;
	display: block;
	white-space: normal;
	overflow-wrap: break-word;
	word-wrap: break-word;
	word-break: break-word;
`;

const DishDetailsTextBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	width: 100%;
`;

const DishDetailsRecipesSection = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 100%;
`;

const DishDetailsRecipesSectionTitle = styled.div`
	font-weight: 600;
	margin-bottom: 0.75rem;
	color: #000;
	font-size: 1.5rem;
`;

const DishDetailsRecipesCard = styled(Card)`
	width: 100%;
	align-items: stretch;
	padding: 1.5rem;
	margin: 0;
	flex-direction: column;
`;

const DishDetailsRecipesTableWrap = styled.div`
	width: 100%;
	overflow-x: auto;
`;

const DishDetailsBodyLoaderWrap = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 12rem;
	width: 100%;
`;

const DishDeleteModalOverlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.35);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: ${zIndex.modalBackdrop};
`;

const DishDeleteModalPanel = styled.div`
	display: flex;
	flex-direction: column;
	background: #ffffff;
	max-width: 28rem;
	width: min(90vw, 28rem);
	border-radius: 10px;
	overflow: hidden;
	box-shadow: rgba(0, 0, 0, 0.15) 0 8px 24px;
`;

const DishDeleteModalHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem 1.25rem;
	background: rgba(45, 35, 66, 0.9);
	color: #fff;
	font-size: 1.1rem;
	font-weight: 600;
`;

const DishDeleteModalClose = styled.button.attrs({ type: "button" })`
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: none;
	padding: 0.25rem;
	cursor: pointer;
	color: #fff;
	opacity: 0.9;
	&:hover {
		opacity: 1;
	}
`;

const DishDeleteModalBody = styled.div`
	padding: 1.25rem;
	color: rgba(0, 0, 0, 0.85);
	line-height: 1.5;
`;

const DishDeleteModalFooter = styled.div`
	display: flex;
	justify-content: flex-end;
	flex-wrap: wrap;
	gap: 0.5rem;
	padding: 0 1.25rem 1.25rem;
`;

function buildDishPrimaryImageUrl(dish) {
	if (!dish?.imageBaseUrl || !dish?.imagePaths?.[0]) {
		return "";
	}
	return `${dish.imageBaseUrl.replace(/\/$/, "")}/${dish.imagePaths[0]}`;
}

function formatDateTime(dateValue) {
	if (!dateValue) {
		return "-";
	}
	const parsedDate = new Date(dateValue);
	if (Number.isNaN(parsedDate.getTime())) {
		return "-";
	}
	return parsedDate.toLocaleString();
}

/** Prefer full cached dish over shallow list prefetch when returning from edit. */
function pickInitialDishForDetails(dishDetailsListPrefetch, cachedDishDetails, routeDishId) {
	if (cachedDishDetails?.dishData?._id === routeDishId) {
		return {
			storedDish: cachedDishDetails.dishData,
			storedRecipes: Array.isArray(cachedDishDetails.recipesData) ? cachedDishDetails.recipesData : [],
			storedCanUpdate: Boolean(cachedDishDetails.canUpdateDish),
			storedCanDelete: Boolean(cachedDishDetails.canDeleteDish),
			needsFetch: false,
		};
	}
	return { storedDish: dishDetailsListPrefetch, storedRecipes: null, storedCanUpdate: false, storedCanDelete: false, needsFetch: true };
}

const DishDetails = () => {
	const { id: dishId } = useParams();
	const navigate = useNavigate();
	const { invalidateDishListCache, dishDetailsListPrefetch, hydrateEditDishDraft, cachedDishDetails, setCachedDishDetails, clearCachedDishDetails } = useDish();
	const { storedDish, storedRecipes, storedCanUpdate, storedCanDelete, needsFetch } = pickInitialDishForDetails(dishDetailsListPrefetch, cachedDishDetails, dishId);
	const [dish, setDish] = useState(storedDish);
	const [dishLinkedRecipes, setDishLinkedRecipes] = useState(storedRecipes);
	const [isDishDetailsLoading, setIsDishDetailsLoading] = useState(needsFetch);
	const [hasDishDetailsError, setHasDishDetailsError] = useState(false);
	const [canUpdateDish, setCanUpdateDish] = useState(storedCanUpdate);
	const [canDeleteDish, setCanDeleteDish] = useState(storedCanDelete);
	const [isDishDeleteSubmitting, setIsDishDeleteSubmitting] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const dishListPath = AppRoutes[DashboardModules.STUDIO][StudioSubModules.DISH].DISH_LIST.getPath();

	function renderDishTagList(tagList) {
		if (!tagList?.length) {
			return null;
		}
		return tagList.map((tagItem, index) => {
			const label = dishTagLabel(tagItem);
			return (
				<DishDetailsTag key={`${tagItem}-${index}`} id={tagItem}>
					{label}
				</DishDetailsTag>
			);
		});
	}

	function navigateToLinkedRecipe(recipeId) {
		const path = AppRoutes[DashboardModules.STUDIO][StudioSubModules.RECIPE].RECIPE_DETAILS.getPath(recipeId);
		navigate(path);
	}

	function handleNavigateBackToDishList() {
		navigate(dishListPath);
		clearCachedDishDetails();
	}

	function handleNavigateToDishEdit() {
		if (!canUpdateDish) {
			return;
		}
		hydrateEditDishDraft(dishId, {
			title: dish.title ?? "",
			description: dish.description ?? "",
			notes: dish.notes ?? "",
			imageUrl: buildDishPrimaryImageUrl(dish),
			tags: dishTagKeysFromApi(dish.tags),
		});
		const path = AppRoutes[DashboardModules.STUDIO][StudioSubModules.DISH].DISH_EDIT.getPath(dishId);
		navigate(path);
	}

	function handleOpenDeleteModal() {
		if (!canDeleteDish || isDishDeleteSubmitting) {
			return;
		}
		setIsDeleteModalOpen(true);
	}

	function handleCloseDeleteModal() {
		if (isDishDeleteSubmitting) {
			return;
		}
		setIsDeleteModalOpen(false);
	}

	async function confirmDeleteDish() {
		if (!canDeleteDish || isDishDeleteSubmitting) {
			return;
		}
		setIsDishDeleteSubmitting(true);
		const { error } = await StudioService.deleteDish(dishId);
		setIsDishDeleteSubmitting(false);
		if (error) {
			errorNotification(typeof error === "string" ? error : "Could not delete dish");
			return;
		}
		setIsDeleteModalOpen(false);
		successNotification("Dish deleted");
		invalidateDishListCache();
		navigate(dishListPath);
	}

	function extractDishFromResponseData(data) {
		if (!data || typeof data !== "object") {
			return null;
		}
		const dishData = data.dish ?? data;
		if (!dishData || typeof dishData !== "object") {
			return null;
		}
		return dishData;
	}

	function extractRecipesFromResponseData(data) {
		if (data && Array.isArray(data.recipes)) {
			return data.recipes;
		}
		return [];
	}

	/**
	 * Loads dish and its linked recipes in parallel, then applies all state and writes the
	 * unified `{ dishData, recipesData }` cache atomically. Linked recipes are non-fatal.
	 */
	async function loadDishDetailsBundle() {
		setIsDishDetailsLoading(true);
		setCanUpdateDish(false);
		setCanDeleteDish(false);
		const [dishResponse, recipesResponse] = await Promise.all([
			StudioService.getDish(dishId),
			StudioService.getRecipesForDish(dishId),
		]);

		const { data: dishResponseData, error: dishError } = dishResponse;
		const { data: recipesResponseData, error: recipesError } = recipesResponse;

		if (dishError) {
			setHasDishDetailsError(true);
			setIsDishDetailsLoading(false);
			errorNotification("Could not get dish data");
			return;
		}
		const dishData = extractDishFromResponseData(dishResponseData);
		if (!dishData) {
			setHasDishDetailsError(true);
			setIsDishDetailsLoading(false);
			errorNotification("Could not get dish data");
			return;
		}
		const recipesData = recipesError ? [] : extractRecipesFromResponseData(recipesResponseData);
		setDish(dishData);
		setCanUpdateDish(dishResponseData.canUpdate);
		setCanDeleteDish(dishResponseData.canDelete);
		setDishLinkedRecipes(recipesData);
		setHasDishDetailsError(false);
		setIsDishDetailsLoading(false);
		setCachedDishDetails({ dishData, recipesData, canUpdateDish: dishResponseData.canUpdate, canDeleteDish: dishResponseData.canDelete });
	}

	useEffect(() => {
		if(needsFetch) {
			loadDishDetailsBundle();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dishId, needsFetch]);

	useEffect(() => {
		setIsDeleteModalOpen(false);
	}, [dishId]);

	function renderRecipesSection() {
		if (!dishLinkedRecipes?.length) {
			return null;
		}
		return (
			<DishDetailsRecipesSection>
				<DishDetailsRecipesSectionTitle>Attached recipes</DishDetailsRecipesSectionTitle>
				<DishDetailsRecipesTableWrap>
					<Table>
						<Trow head top="0">
							<Thead textAlign="left">Name</Thead>
							<Thead>Created At</Thead>
							<Thead>Updated At</Thead>
							<Thead>View</Thead>
						</Trow>
						<Tbody>
							{dishLinkedRecipes.map((recipe) => (
								<Trow hoverEffect={false} key={recipe._id}>
									<Tcol textAlign="left" title="Name" borderRadius="10px 0 0 10px">
										{recipe.name}
									</Tcol>
									<Tcol title="Created At">{formatDateTime(recipe.createdAt)}</Tcol>
									<Tcol title="Updated At">{formatDateTime(recipe.updatedAt)}</Tcol>
									<Tcol title="View" borderRadius="0 10px 10px 0">
										<Button type="button" onClick={() => navigateToLinkedRecipe(recipe._id)}>View</Button>
									</Tcol>
								</Trow>
							))}
						</Tbody>
					</Table>
				</DishDetailsRecipesTableWrap>
			</DishDetailsRecipesSection>
		);
	}

	function renderDishBody(dishData) {
		const imageUrl = buildDishPrimaryImageUrl(dishData);
		return (
			<>
				<DishDetailsHeroRow>
					<DishDetailsImageColumn>
						{imageUrl ? <DishDetailsHeroImage src={imageUrl} alt={dishData.title} /> : null}
					</DishDetailsImageColumn>
					<DishDetailsTextColumn>
						<DishDetailsTextBlock>
							<DishDetailsFieldLabel>Description</DishDetailsFieldLabel>
							<DishDetailsFieldValue>{dishData.description ?? "\xa0 -"}</DishDetailsFieldValue>
						</DishDetailsTextBlock>
						<DishDetailsTextBlock>
							<DishDetailsFieldLabel>Notes</DishDetailsFieldLabel>
							<DishDetailsFieldValue>{dishData.notes ?? "\xa0 -"}</DishDetailsFieldValue>
						</DishDetailsTextBlock>
						<DishDetailsTextBlock>
							<DishDetailsFieldLabel>Tags</DishDetailsFieldLabel>
							<DishDetailsFieldValue>
								{dishData.tags?.length ? (
									<DishDetailsTagList>{renderDishTagList(dishData.tags)}</DishDetailsTagList>
								) : "-"}
							</DishDetailsFieldValue>
						</DishDetailsTextBlock>
					</DishDetailsTextColumn>
					<DishDetailsActionsColumn>
						<Button onClick={handleNavigateToDishEdit} disabled={!canUpdateDish || isDishDeleteSubmitting}>
							Edit
						</Button>
						<DishDetailsDeleteButton onClick={handleOpenDeleteModal} disabled={!canDeleteDish || isDishDeleteSubmitting}>
							Delete
						</DishDetailsDeleteButton>
					</DishDetailsActionsColumn>
				</DishDetailsHeroRow>
			</>
		);
	}
	const showLoadingShell = isDishDetailsLoading && !hasDishDetailsError;
	const showFullDish = !hasDishDetailsError && !isDishDetailsLoading;

	function renderPageHeaderBar(titleText) {
		return (
			<DishDetailsPageHeader>
				<DishDetailsCardHeaderLeft>
					<DishDetailsCardHeaderBackButton onClick={handleNavigateBackToDishList}>arrow_back</DishDetailsCardHeaderBackButton>
					<DishDetailsHeaderTitle title={titleText || undefined}>{titleText || "\xa0"}</DishDetailsHeaderTitle>
				</DishDetailsCardHeaderLeft>
			</DishDetailsPageHeader>
		);
	}

	return (
		<Page>
			<DishDetailsContentColumn>
				{renderPageHeaderBar(dish?.title)}
				{showLoadingShell && (
					<DishDetailsMainCard>
						<DishDetailsBodyLoaderWrap>
							<Loader />
						</DishDetailsBodyLoaderWrap>
					</DishDetailsMainCard>
				)}
				{showFullDish ? <>
					<DishDetailsMainCard>{renderDishBody(dish)}</DishDetailsMainCard>
					{renderRecipesSection()}
				</> : ""}
			</DishDetailsContentColumn>
			{hasDishDetailsError ? <EmptyState retry={loadDishDetailsBundle} /> : null}
			{isDeleteModalOpen && (
				<DishDeleteModalOverlay
					role="presentation"
					onClick={(event) => {
						if (event.target === event.currentTarget) {
							handleCloseDeleteModal();
						}
					}}
				>
					<DishDeleteModalPanel role="dialog" aria-modal="true" aria-labelledby="dish-delete-modal-title">
						<DishDeleteModalHeader>
							<span id="dish-delete-modal-title">Delete dish</span>
							<DishDeleteModalClose disabled={isDishDeleteSubmitting} onClick={handleCloseDeleteModal} aria-label="Close dialog">
								<span className="material-symbols-outlined">close</span>
							</DishDeleteModalClose>
						</DishDeleteModalHeader>
						<DishDeleteModalBody>
							Delete {dish.title}? This cannot be undone.
						</DishDeleteModalBody>
						<DishDeleteModalFooter>
							<Button type="button" onClick={handleCloseDeleteModal} disabled={isDishDeleteSubmitting}>
								Cancel
							</Button>
							<DishDetailsDeleteButton type="button" onClick={confirmDeleteDish} disabled={isDishDeleteSubmitting}>
								{isDishDeleteSubmitting ? "Deleting…" : "Delete"}
							</DishDetailsDeleteButton>
						</DishDeleteModalFooter>
					</DishDeleteModalPanel>
				</DishDeleteModalOverlay>
			)}
		</Page>
	);
};

export default DishDetails;
